import { useState } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import find from 'lodash/find';

import Icon, { Icons } from '../icons/Icon';

type SelectFieldOption = {
  label: string;
  value: string;
};

type SelectFieldProps = {
  id?: string;
  name: string;
  label: string;
  options: SelectFieldOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  readonly?: boolean;
  onChange?: () => void;
};

function SelectField({
  className,
  label,
  onChange,
  options,
  placeholder = '',
  ...props
}: SelectFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [field, meta, helper] = useField<string>({ ...props });
  const { disabled = false, readonly = false } = props;
  const isEnabled = !disabled && !readonly;
  const hasError = meta.touched && meta.error;

  const selectedLabel = (): string => {
    if (!field.value) {
      return placeholder;
    } else {
      const option = find(options, { value: field.value });
      return !!option ? option.label : '';
    }
  };

  const selectValue = (value: string): void => {
    toggleExpanded();
    if (isEnabled && value !== field.value) {
      helper.setValue(value, true);
      !!onChange && onChange();
    }
  };

  const toggleExpanded = (): void => {
    if (isEnabled) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={classNames('relative', className)} data-testid="select-field">
      {/* Field */}
      <div
        className="flex cursor-pointer items-stretch rounded bg-slate-200 dark:bg-slate-800"
        onClick={toggleExpanded}
      >
        <div className="grow p-2">
          <div className="flex flex-col">
            <div className="mb-1 text-sm">{label}</div>
            <div className="font-bold" data-testid="select-field-selected-option">
              {selectedLabel()}
            </div>
          </div>
        </div>
        <div className="flex w-8 items-center justify-center">
          {isExpanded && <Icon name={Icons.ChevronUp} fixedWidth />}
          {!isExpanded && <Icon name={Icons.ChevronDown} fixedWidth />}
        </div>
      </div>

      {/* Options */}
      <div
        className={classNames(
          'sladow-slate-900 absolute top-16 z-10 max-h-64 w-full overflow-y-auto rounded-b bg-slate-100 py-2 shadow-lg dark:bg-slate-900 dark:shadow-slate-800/70',
          {
            hidden: !isExpanded,
          },
        )}
      >
        {options.map((option, index) => (
          <div
            key={index}
            className={classNames(
              'cursor-pointer px-2 py-4 hover:bg-slate-200 dark:hover:bg-slate-800',
              { 'bg-slate-200 dark:bg-slate-800': field.value === option.value },
            )}
            onClick={() => selectValue(option.value)}
            data-testid="select-field-option"
          >
            {option.label}
          </div>
        ))}
      </div>

      {/* Error state */}
      {hasError && <div className="my-1 text-red-800">{meta.error}</div>}
    </div>
  );
}

export default SelectField;
